import * as React from "react";
import { RegionMapChart } from "./MapChart";
import {
    Country,
    TotalMetricInfo,
    lookupRegionInfo,
    AppState,
    findCountryIsoCodeForGeographicReportSelector,
    reportClicksAndImpressions,
    currentReportCampaign
} from "@store";
import {
    ChartDataResponse,
    MetricInfo,
    campaignMetrics,
    makeBinMetric,
    ChartMetricData,
    MetricLabels
} from "@api/common";
import { ReportLabel } from "./ReportLabel";
import { LegendItem } from "./LegendItem";
import { connect } from "react-redux";
import { FetchingState } from "rd-redux-http";
import "./GeoReport.css";
import * as d3 from "d3";
import { formatNumber } from "@common";

interface GeoReportMapComponentProps {
    chart: ChartDataResponse;

    countries: Country[];
    geographicSvg: string;
    metrics: MetricInfo[];
    totals: TotalMetricInfo[];
    selectedCountryIsoCode: string;
}

export interface GeoReportMapComponentState {
    displayedMetric: string;

    tooltipVisible: boolean;
    tooltipRegion: string;
    clientPoint: { x: number; y: number };
    hoverBin: number;
}

class GeoReportMapComponent extends React.Component<GeoReportMapComponentProps, GeoReportMapComponentState> {
    render() {
        const { chart, metrics, selectedCountryIsoCode, countries } = this.props;
        const selectedCountry = countries.find(country => country.iso === selectedCountryIsoCode);
        return (
            <div
                className="GeoReport"
                // onMouseEnter={_ => this.setState({ tooltipVisible: true })}
                // onMouseLeave={_ => this.setState({ tooltipVisible: false })}
                // onMouseMove={e => {
                //     const rect = e.currentTarget.getBoundingClientRect();
                //     this.setState({
                //         tooltipRegion: e.target ? e.target["id"] : undefined,
                //         clientPoint: { x: e.clientX - rect.left + 10, y: e.clientY - rect.top + 10 }
                //     });
                // }}
            >
                <div className="report-head">
                    <ReportLabel title="Geo Report" subtitle={selectedCountry && selectedCountry.name} />
                    <div className="legend">
                        {metrics
                            .filter(m => !m.isRate)
                            .filter(m => chart.metrics.some(c => c.metric === m.metric))
                            .map(m => (
                                <LegendItem
                                    color={m.color}
                                    checked={this.getDisplayedMetric().metric === m.metric}
                                    onChange={() => this.setState({ displayedMetric: m.metric })}
                                    key={`GeoReport-LegendItem-${m.metric}`}
                                >
                                    {MetricLabels.metricSwitcher(m)}
                                </LegendItem>
                            ))}
                    </div>
                </div>
                <div className="GeoReport__content">
                    <div className="GeoReport__totals">{this.renderTotalCounts()}</div>
                    <div className="GeoReport__map-container">{this.renderMap()}</div>
                </div>
                {/* {this.renderTooltip()} */}
            </div>
        );
    }

    renderMap() {
        const chart = this.props.chart.metrics.find(
            c => c.metric === makeBinMetric(this.getDisplayedMetric().metric, 6)
        );
        const info = this.props.metrics.find(m => m.metric === this.getDisplayedMetric().metric);
        const binColors = this.binColors(chart, info.color);

        return (
            <>
                {this.state &&
                    this.state.hoverBin !== undefined && (
                        <style
                            dangerouslySetInnerHTML={{
                                __html: `.GeoReport__map svg path.bin-${
                                    this.state.hoverBin
                                } { fill: #FF8243 !important; }`
                            }}
                        />
                    )}
                <div className="GeoReport__map">
                    {this.props.geographicSvg && (
                        <RegionMapChart
                            countries={this.props.countries}
                            region={chart}
                            binColors={binColors}
                            geographicSvg={this.props.geographicSvg}
                        />
                    )}
                </div>
                <div className="GeoReport__map-legend-container">
                    <div className="GeoReport__map-legend">{this.renderMapLegend(chart, binColors)}</div>
                </div>
            </>
        );
    }

    private renderMapLegend(data: ChartMetricData, binColors: string[]) {
        if (!data || !data.bins || !data.bins.length) {
            return null;
        }

        return (
            <div className="GeoReportMapLegend">
                {data.bins.map(b => (
                    <div
                        className="GeoReportMapLegend__bin"
                        style={{ background: binColors[b.index] }}
                        key={b.index}
                        onMouseEnter={() => this.setState({ hoverBin: b.index })}
                        onMouseLeave={() => this.setState({ hoverBin: undefined })}
                    >
                        <div className="GeoReportMapLegend__bound min">{formatNumber(b.min)}</div>
                        <div className="GeoReportMapLegend__bound max">{formatNumber(b.max)}</div>
                    </div>
                ))}
            </div>
        );
    }

    private binColors(data: ChartMetricData, baseColor: string): string[] {
        if (!data || !data.bins) {
            return [];
        }

        const lowColor = d3.interpolateHcl("#FFFFFF", baseColor)(0.08);

        const colorScale = d3
            .scaleLinear<string>()
            .domain(d3.extent(data.bins || [], d => d.index))
            .range([lowColor, baseColor])
            .interpolate(d3.interpolateHcl);

        const result: string[] = [];

        (data.bins || []).forEach(b => (result[b.index] = colorScale(b.index)));

        return result;
    }

    private renderTotalCounts() {
        return (
            <div className="GeoReportTotal">
                {this.props.totals.map(t => (
                    <div className="GeoReportTotal__item" key={`GeoReportTotal-${t.name}`}>
                        <div className="GeoReportTotal__label">{t.name}</div>
                        <div className="GeoReportTotal__value" style={{ color: t.color }}>
                            {formatNumber(t.value)}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    renderTooltip() {
        if (!this.state || !this.state.tooltipVisible) {
            return undefined;
        }

        const region = this.state.tooltipRegion;

        if (!region) {
            return undefined;
        }

        const chart = this.props.chart.metrics.find(
            c => c.metric === makeBinMetric(this.getDisplayedMetric().metric, 6)
        );

        if (!chart) {
            return undefined;
        }

        const data = chart.data.find(d => d["dimension-values"][0] === region);

        if (!data) {
            return undefined;
        }

        const bin = (chart.bins || []).find(b => b.index === data["metric-value"]);
        const regionInfo = lookupRegionInfo(this.props.countries, region);

        if (!regionInfo) {
            return undefined;
        }

        return (
            <div
                className="GeoReportTooltip"
                style={{ left: this.state.clientPoint.x, top: this.state.clientPoint.y }}
            >{`${regionInfo.countryName}, ${regionInfo.regionName}: ${bin.min}-${bin.max}`}</div>
        );
    }

    private getDisplayedMetric(): MetricInfo {
        const metric =
            this.props.metrics.find(
                m => m.metric === (this.state ? this.state.displayedMetric : this.props.metrics[0].metric)
            ) || this.props.metrics[0];

        return metric;
    }
}

export const GeoReportMap = connect(
    (appState: AppState): GeoReportMapComponentProps => {
        const campaign = currentReportCampaign(appState);

        const metrics = campaignMetrics(campaign ? campaign.unit : "CPM").filter(m => !m.isRate);

        const areas = FetchingState.getDataOrDefault(appState.reports.countries, []);

        const regionData = FetchingState.getDataOrDefault(appState.reports.chartData.regionData, {
            dimensions: [],
            metrics: []
        });

        const geographicReportData = appState.reports.geographicReport;
        const selectedCountryIsoCode = findCountryIsoCodeForGeographicReportSelector(appState);

        return {
            countries: areas,
            chart: regionData,
            geographicSvg:
                selectedCountryIsoCode &&
                appState.reports.geographicReport.countrySVGs &&
                geographicReportData.countrySVGs[selectedCountryIsoCode],
            totals: reportClicksAndImpressions(appState),
            metrics,
            selectedCountryIsoCode
        };
    }
)(GeoReportMapComponent);
